import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import * as moment from 'moment';
import { LeadsComunicationService } from '../../../pages/leads/leads-comunication.service';
import { Interactions } from '../../constants/Interactions';
import { CreateEventInput } from '../../models/calendar/create-event.input';
import { ArchiveLeadInput } from '../../models/lead/archiveLead.input';
import { CloseLeadInput } from '../../models/lead/closeLead.input.1';
import { FinishCallInput } from '../../models/lead/finishCall.input';
import { ReminderLeadInput } from '../../models/lead/reminderLead.input';
import { TransitionToOnTrackInput } from '../../models/lead/transitionToTrack.input';
import { UnAssignLeadsInput } from '../../models/lead/unAssignLeads.input';
import { VisitConfirmationInput } from '../../models/lead/visitConfirmation.input';
import { VisitLeadInput } from '../../models/lead/visitLead.input';
import { ValidateMinDate } from '../../validator/min-date.validator';
import { CalendarService } from '../calendar/calendar.service';
import { AuthService } from '../security/auth.service';
import { DateService } from '../utils/date.service';
import { RevertCloseLeadInput } from './../../models/lead/revertCloseLead.input';
import { LeadService } from './../lead/lead.service';

@Injectable({
  providedIn: 'root'
})
export class FormsService {

  constructor(private readonly formBuilder: FormBuilder,
    private readonly leadService: LeadService,
    private readonly calendarService: CalendarService,
    private readonly dateService: DateService,
    private readonly authService: AuthService,
    private readonly leadComunicationService: LeadsComunicationService
  ) {
  }

  initArchiveForm(): FormGroup {
    return this.formBuilder.group({
      archivedOption: ['', Validators.required],
      comments: [{ value: '' }],
    });
  }

  initFormRevertClose(): FormGroup {
    return this.formBuilder.group({
      comments: ['', Validators.required],
    });
  }

  initFormClose(closeDate: moment.Moment = null): FormGroup {
    return this.formBuilder.group({
      closeDate: [closeDate ? closeDate.format('YYYY-MM-DDTHH:mm') : moment()
        .format('YYYY-MM-DDTHH:mm'), [Validators.required]],
      comments: ['', Validators.required]
    });
  }

  initFinishCallForm(): FormGroup {
    return this.formBuilder.group({
      onTrackStatus: ['', Validators.required],
      comments: ['', Validators.required]
    });
  }

  initFormReminder(reminderDate: moment.Moment = null, defaultReminder?: boolean): FormGroup {
    return this.formBuilder.group({
      reminderDate: [reminderDate ? reminderDate.format('YYYY-MM-DDTHH:mm') : moment()
        .format('YYYY-MM-DDTHH:mm'),
      [Validators.required, ValidateMinDate]],
      dateSelected: [defaultReminder ? 'onehour' : ''],
      comments: ['']
    });
  }

  initFormVisit(visitDate?: moment.Moment): FormGroup {
    return this.formBuilder.group({
      visitDate: [visitDate ? visitDate.format('YYYY-MM-DDTHH:mm') : moment()
        .format('YYYY-MM-DDTHH:mm'),
      [Validators.required, ValidateMinDate]],
      comments: ['']
    });
  }

  initCalendarEvent(dateFrom?: moment.Moment): FormGroup {
    return this.formBuilder.group({
      dateFrom: [dateFrom ? dateFrom.format('YYYY-MM-DDTHH:mm') : moment()
        .format('YYYY-MM-DDTHH:mm'),
      [Validators.required, ValidateMinDate]],
      comments: [''],
      userId: [''],
      type: [''],
      confirmAction: [false]
    });
  }

  changeArchiveOption(form: FormGroup, event: any): void {
    // TODO Code duplicate src/app/pages/modals/archived-modal/archived-modal.component.ts
    const formControls = form.controls;
    formControls.comments.enable();
  }

  async sendFinishCallForm(finishCallInput: FinishCallInput): Promise<any> {
    return new Promise((resolve, reject) => {
      this.leadService.finishCall$(finishCallInput)
        .subscribe(result => {
          if (result) {
            resolve(true);
          } else {
            reject();
          }
        }, err => {
          reject(err);
        });
    });

  }

  async sendTransitionToTrack(transitionToTrackInput: TransitionToOnTrackInput): Promise<any> {
    return new Promise((resolve, reject) => {
      this.leadService.transitionToTrack$(transitionToTrackInput)
        .subscribe(result => {
          if (result) {
            resolve(true);
          } else {
            reject();
          }
        }, err => {
          reject(err);
        });
    });

  }

  async sendVisitConfirmation(visitConfirmationInput: VisitConfirmationInput): Promise<any> {
    return new Promise((resolve, reject) => {
      this.leadService.visitConfirmation$(visitConfirmationInput)
        .subscribe(result => {
          if (result) {
            resolve(true);
          } else {
            reject();
          }
        }, err => {
          reject(err);
        });
    });

  }

  async sendArchiveForm(archiveLeadInput: ArchiveLeadInput): Promise<any> {
    return new Promise<void>((resolve, reject) => {
      this.leadService.archiveLead$(archiveLeadInput)
        .subscribe(() => {
          resolve();
        }, error => {
          reject(error);
        });
    });
  }

  async sendCloseForm(closeLeadInput: CloseLeadInput): Promise<any> {
    return new Promise<void>((resolve, reject) => {
      this.leadService.closeLead$(closeLeadInput)
        .subscribe(() => {
          resolve();
        }, error => {
          reject(error);
        });

    });
  }

  async sendReminderForm(reminderLeadInput: ReminderLeadInput): Promise<any> {
    return new Promise<void>((resolve, reject) => {
      this.leadService.reminderLead$(reminderLeadInput)
        .subscribe(() => {
          resolve();
        }, error => {
          reject(error);
        });

    });
  }

  async sendVisitForm(visitLeadInput: VisitLeadInput): Promise<any> {
    return new Promise<void>((resolve, reject) => {
      this.leadService.visitLead$(visitLeadInput)
        .subscribe(() => {
          resolve();
        }, error => {
          reject(error);
        });

    });
  }

  async sendCalendarEventForm(createEventInput: CreateEventInput): Promise<any> {
    return new Promise<void>((resolve, reject) => {
      this.calendarService.createEvent$(createEventInput)
        .subscribe(() => {
          resolve();
        }, error => {
          reject(error);
        });

    });
  }
  async sendCalendarPrequalificationEventForm(createEventInput: CreateEventInput): Promise<any> {
    return new Promise<void>((resolve, reject) => {
      this.calendarService.createEventWithoutUser$(createEventInput)
        .subscribe(() => {
          resolve();
        }, error => {
          reject(error);
        });

    });
  }

  bindArchiveForm(leadsId: string, form: FormGroup, extraParam: any, comingFromCallModal?: boolean): ArchiveLeadInput {
    const archiveInput = new ArchiveLeadInput();
    archiveInput.leadsId = [leadsId];
    archiveInput.archivedOption = form.controls.archivedOption.value;
    archiveInput.comments = this.removeTagHtml(form.controls.comments.value);
    archiveInput.comingFromCallModal = comingFromCallModal;

    return archiveInput;
  }

  bindRevertClose(leadId: string, form: FormGroup): RevertCloseLeadInput {
    const revertCloseLeadInput = new RevertCloseLeadInput();
    revertCloseLeadInput.leadId = leadId;
    revertCloseLeadInput.comments = this.removeTagHtml(form.controls.comments.value);

    return revertCloseLeadInput;
  }

  bindCloseForm(leadId: string, form: FormGroup, comingFromCallModal?: boolean): CloseLeadInput {
    const closeLeadInput = new CloseLeadInput();
    closeLeadInput.comments = this.removeTagHtml(form.controls.comments.value);
    closeLeadInput.closeDate = form.controls.closeDate.value;
    closeLeadInput.leadId = leadId;
    closeLeadInput.comingFromCallModal = comingFromCallModal;

    return closeLeadInput;
  }

  bindCalendarForm(leadId: string, form: FormGroup, userId): CreateEventInput {
    const calendarEventInput = new CreateEventInput();
    calendarEventInput.leadId = leadId;
    calendarEventInput.userId = form.controls.userId.value;
    calendarEventInput.type = form.controls.type.value;
    calendarEventInput.dateFrom = form.controls.dateFrom.value ?
      this.dateService.transformDateInputToDate(form.controls.dateFrom.value)
        .toDate() : null;
    calendarEventInput.text = this.removeTagHtml(form.controls.comments.value);
    return calendarEventInput;
  }

  bindReminderForm(leadId: string, form: FormGroup): ReminderLeadInput {
    const reminderLeadInput = new ReminderLeadInput();
    reminderLeadInput.leadId = leadId;
    reminderLeadInput.reminderDate = form.controls.reminderDate.value ?
      this.dateService.transformDateInputToDate(form.controls.reminderDate.value)
        .toDate() : null;
    reminderLeadInput.comments = this.removeTagHtml(form.controls.comments.value);

    return reminderLeadInput;
  }

  bindVisitForm(leadId: string, form: FormGroup): VisitLeadInput {
    const visitLeadInput = new VisitLeadInput();
    visitLeadInput.leadId = leadId;
    visitLeadInput.visitDate = form.controls.visitDate.value ?
      this.dateService.transformDateInputToDate(form.controls.visitDate.value)
        .toDate() : null;
    visitLeadInput.comments = this.removeTagHtml(form.controls.comments.value);

    return visitLeadInput;
  }

  bindVisitConfirmationForm(leadId: string, form: FormGroup, eventId: string): VisitConfirmationInput {

    const visitConfirmationInput = new VisitConfirmationInput();
    visitConfirmationInput.leadId = leadId;
    visitConfirmationInput.eventId = eventId;
    visitConfirmationInput.visitConfirmationStatus = form.value.visitConfirmationStatus;
    visitConfirmationInput.onTrackStatus = form.value.onTrackStatus;
    visitConfirmationInput.comments = this.removeTagHtml(form.value.comments);

    return visitConfirmationInput;
  }

  bindTransitionToTrackForm(leadId: string, form: FormGroup): TransitionToOnTrackInput {
    const transitionToTrackInput = new TransitionToOnTrackInput();
    transitionToTrackInput.leadId = leadId;
    transitionToTrackInput.onTrackStatus = form.controls.onTrackStatus.value;
    transitionToTrackInput.comments = this.removeTagHtml(form.controls.comments.value);

    return transitionToTrackInput;
  }

  bindFinishCallForm(leadId: string, form: FormGroup, callReportTime: any): FinishCallInput {
    const finishCallInput = new FinishCallInput();
    finishCallInput.leadId = leadId;

    if (form.controls.onTrackStatus.value === Interactions.CALL_WITHOUT_ANSWER) {
      finishCallInput.onTrackStatus = null;
      finishCallInput.resultCall = Interactions.CALL_WITHOUT_ANSWER;
    } else {
      finishCallInput.onTrackStatus = form.controls.onTrackStatus.value;
      finishCallInput.resultCall = Interactions.CALL_ANSWER;
    }
    finishCallInput.reportTime = callReportTime === null ? 0 : callReportTime;
    finishCallInput.comments = this.removeTagHtml(form.controls.comments.value);

    return finishCallInput;
  }

  bindForm(leadId: string, interactionSelected: string, form: FormGroup, extraParam?: any, comingFromCallModal?: boolean): any {
    let input: any;

    switch (interactionSelected) {
      case Interactions.ARCHIVE:
        input = this.bindArchiveForm(leadId, form, extraParam, comingFromCallModal);
        break;
      case Interactions.CLOSE:
        input = this.bindCloseForm(leadId, form, comingFromCallModal);
        break;
      case Interactions.FINISH_CALL:
        input = this.bindFinishCallForm(leadId, form, extraParam);
        break;
      case Interactions.REMINDER:
        input = this.bindReminderForm(leadId, form);
        break;
      case Interactions.VISIT:
        input = this.bindVisitForm(leadId, form);
        break;
      case Interactions.VISIT_CONFIRMATION:
        input = this.bindVisitConfirmationForm(leadId, form, extraParam);
        break;
      case Interactions.TRANSITION_TO_ON_TRACK:
        input = this.bindTransitionToTrackForm(leadId, form);
        break;
      case Interactions.CALENDAR:
        input = this.bindCalendarForm(leadId, form, extraParam);
        break;
      case Interactions.CALENDAR_PREQUALIFICATION:
        input = this.bindCalendarForm(leadId, form, extraParam);
        break;
      case Interactions.REVERT_CLOSE:
        input = this.bindRevertClose(leadId, form);
        break;
      default:
        break;
    }

    return input;
  }

  async sendForm(
    leadId: string,
    interactionSelected: string,
    form: FormGroup,
    extraParam?: any,
    comingFromCallModal?: boolean
  ): Promise<void> {
    const input = this.bindForm(leadId, interactionSelected, form, extraParam, comingFromCallModal);
    let promise: Promise<void>;
    switch (interactionSelected) {
      case Interactions.ARCHIVE:
        promise = this.sendArchiveForm(input);
        break;
      case Interactions.CLOSE:
        promise = this.sendCloseForm(input);
        break;
      case Interactions.REMINDER:
        promise = this.sendReminderForm(input);
        break;
      case Interactions.VISIT:
        promise = this.sendVisitForm(input);
        break;
      case Interactions.CALENDAR:
        promise = this.sendCalendarEventForm(input);
        break;
      case Interactions.CALENDAR_PREQUALIFICATION:
        const unAssignInput = new UnAssignLeadsInput();
        unAssignInput.leadIds = [leadId];
        unAssignInput.comments = form.value.comments;
        unAssignInput.comingFromCallModal = comingFromCallModal;

        if (!extraParam) {
          promise = this.sendCalendarPrequalificationEventForm(input).then(() =>
            this.leadComunicationService.unAssignLead.emit(unAssignInput)
          );
        } else {
          this.leadComunicationService.unAssignLead.emit(unAssignInput);
        }
        break;
      default:
        break;
    }

    return promise;
  }

  removeTagHtml(value: string): string {
    const regex = new RegExp('<[^a \/]');

    return value ? value.replace(regex, found => found.replace('<', '< ')) : '';
  }

}
